import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Globe, Menu, X, ChevronDown } from "lucide-react";
import { useLang, LANGS } from "@/App";
import { all } from "@/data/images";

const ITEMS = [
  ["inicio", ""],
  ["habitaciones", "habitaciones"],
  ["gastronomia", "gastronomia"],
  ["servicios", "servicios"],
  ["eventos", "eventos"],
  ["queHacer", "que-hacer"],
  ["fotos", "fotos"],
  ["contacto", "contacto"],
];

export default function Header() {
  const { t, lang, L, cambiarIdioma } = useLang();
  const { pathname } = useLocation();
  const [abierto, setAbierto] = useState(false);
  const [langAbierto, setLangAbierto] = useState(false);
  const [logo, setLogo] = useState(null);

  useEffect(() => {
    const opciones = all("logo");
    if (opciones.length) setLogo(opciones.find((u) => u.endsWith(".png")) || opciones[0]);
  }, []);

  useEffect(() => {
    setAbierto(false);
    setLangAbierto(false);
  }, [pathname]);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 border-b border-lineas bg-sand/95 backdrop-blur-md"
      data-testid="header"
    >
      <div className="mx-auto flex h-16 max-w-[1360px] items-center justify-between px-5 md:h-20 md:px-8">
        <Link to={L("")} className="flex shrink-0 items-center gap-3" data-testid="header-logo">
          {logo ? (
            <img src={logo} alt="La Carla Posada Boutique" className="h-9 w-auto md:h-10" />
          ) : (
            <>
              <span className="flex h-9 w-9 items-center justify-center border border-ochre/60 font-serif text-lg text-terra md:h-11 md:w-11">
                LC
              </span>
              <span className="hidden font-serif text-xl tracking-wide text-ink sm:block md:text-2xl">
                La Carla <span className="text-ochre">Posada Boutique</span>
              </span>
            </>
          )}
        </Link>

        <nav className="hidden items-center gap-5 2xl:gap-7 xl:flex" data-testid="header-nav">
          {ITEMS.map(([k, to]) => (
            <NavLink
              key={k}
              to={L(to)}
              end={to === ""}
              className={({ isActive }) =>
                `whitespace-nowrap text-[11.5px] font-semibold uppercase tracking-[0.16em] transition-colors duration-200 ${
                  isActive ? "text-terra" : "text-ink-soft hover:text-terra"
                }`
              }
              data-testid={`nav-${k}`}
            >
              {t.nav[k]}
            </NavLink>
          ))}
        </nav>

        <div className="flex shrink-0 items-center gap-2.5 md:gap-4">
          <a
            href="https://soybamo.com/finca-la-carla"
            className="hidden bg-terra px-5 py-2.5 text-[11px] font-bold tracking-[0.2em] text-sand transition-all duration-300 hover:bg-terra-dark sm:inline-flex"
            data-testid="header-reservar-btn"
          >
            {t.nav.reservar}
          </a>

          <div className="relative">
            <button
              onClick={() => setLangAbierto((v) => !v)}
              className="flex items-center gap-1.5 border border-ink/20 px-3 py-2 text-[11px] font-bold tracking-widest text-ink-soft transition-colors hover:border-terra hover:text-terra"
              data-testid="header-idioma-selector"
            >
              <Globe size={15} strokeWidth={1.6} />
              {lang.toUpperCase()}
              <ChevronDown size={13} className={`transition-transform ${langAbierto ? "rotate-180" : ""}`} />
            </button>
            {langAbierto && (
              <div className="absolute right-0 top-full mt-2 w-40 border border-lineas bg-sand py-2 shadow-xl shadow-ink/10">
                {LANGS.map((l) => (
                  <a
                    key={l}
                    href={cambiarIdioma(l)}
                    className={`block px-4 py-2 text-xs font-semibold tracking-widest transition-colors ${
                      l === lang ? "text-terra" : "text-ink-soft hover:text-terra"
                    }`}
                    data-testid={`idioma-${l}`}
                  >
                    {dict_nombre(l)}
                  </a>
                ))}
              </div>
            )}
          </div>

          <button
            className="text-ink xl:hidden"
            onClick={() => setAbierto((v) => !v)}
            data-testid="header-menu-mobile-btn"
            aria-label="Menú"
          >
            {abierto ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {abierto && (
        <div className="border-t border-lineas bg-sand xl:hidden" data-testid="header-nav-mobile">
          <nav className="flex flex-col px-6 py-4">
            {ITEMS.map(([k, to]) => (
              <Link
                key={k}
                to={L(to)}
                className="border-b border-lineas/60 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] text-ink-soft"
                data-testid={`nav-mobile-${k}`}
              >
                {t.nav[k]}
              </Link>
            ))}
            <a
              href="https://soybamo.com/finca-la-carla"
              className="my-4 bg-terra px-5 py-3 text-center text-[11px] font-bold tracking-[0.2em] text-sand"
              data-testid="header-reservar-mobile-btn"
            >
              {t.nav.reservar}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}

function dict_nombre(l) {
  return { es: "Español", en: "English", fr: "Français", it: "Italiano" }[l];
}